import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const normalise = (value) => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const sizeKey = (value) => normalise(value).replace(/,/g, '.').replace(/\b(anos?|years?)\b/g, 'y').replace(/\b(mes|meses|months?)\b/g, 'm');

/** @extends {Component<{details: HTMLDetailsElement, form: HTMLFormElement, size: HTMLSelectElement, submit: HTMLButtonElement, status: HTMLElement, results: HTMLUListElement, availableSizes: HTMLScriptElement, strings: HTMLScriptElement}>} */
class SizeAlternatives extends Component {
  requiredRefs = ['details', 'form', 'size', 'submit', 'status', 'results', 'availableSizes', 'strings'];
  #lifecycle;
  #request;
  #ready = false;
  #strings;
  #base;
  #sizeParam;
  #ownSizes;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#request?.abort();
    this.#selection++;
    super.disconnectedCallback();
  }

  #setup() {
    this.#lifecycle?.abort();
    this.#request?.abort();
    this.#lifecycle = new AbortController();
    this.#ready = false;
    this.#selection++;
    this.#strings = JSON.parse(this.refs.strings.textContent);
    this.#ownSizes = new Set(JSON.parse(this.refs.availableSizes.textContent).map(sizeKey));
    this.refs.form.hidden = true;
    this.refs.details.addEventListener('toggle', () => {
      if (this.refs.details.open && !this.#ready) this.#loadSizes();
      if (!this.refs.details.open) this.clearResults();
    }, { signal: this.#lifecycle.signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card') || event.target.dataset.productId !== this.dataset.productId) return;
      const selection = ++this.#selection;
      this.clearResults();
      event.promise?.then(({ detail }) => {
        if (this.isConnected && selection === this.#selection && detail?.resource?.price != null) this.dataset.price = String(detail.resource.price);
      }).catch(() => {});
    }, { signal: this.#lifecycle.signal });
    if (this.refs.details.open) this.#loadSizes();
  }

  async #options(url, signal) {
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', 'bp-search-intent-options');
    const response = await fetch(endpoint, { signal });
    if (!response.ok) throw new Error('options');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = html.querySelector('[data-bp-intent-options]');
    if (!root) throw new Error('options');
    return [...root.querySelectorAll('[data-role]')].map((filter) => ({
      role: filter.dataset.role, param: filter.dataset.param,
      values: [...filter.querySelectorAll('[data-value]')].map((value) => ({ value: value.dataset.value, label: value.dataset.label, count: Number(value.dataset.count) })),
    }));
  }

  async #loadSizes() {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;
    this.refs.status.textContent = this.#strings.loading;
    try {
      const base = new URL(this.dataset.collectionUrl, location.origin);
      if (base.origin !== location.origin || !/\/collections\/[a-z0-9-]+\/?$/.test(base.pathname)) throw new Error('collection');
      const filters = await this.#options(base, controller.signal);
      const availability = filters.find((filter) => filter.role === 'availability');
      if (!availability?.values.some((value) => value.value === '1')) throw new Error('availability');
      base.searchParams.set(availability.param, '1');
      const types = filters.find((filter) => filter.role === 'type');
      const type = types?.values.find((value) => normalise(value.value) === normalise(this.dataset.productType));
      if (type) base.searchParams.set(types.param, type.value);
      const scoped = await this.#options(base, controller.signal);
      if (!this.isConnected || controller.signal.aborted) return;
      const sizes = scoped.find((filter) => filter.role === this.dataset.kind);
      if (!sizes?.param) throw new Error('sizes');
      this.#base = base;
      this.#sizeParam = sizes.param;
      this.refs.size.replaceChildren(new Option(this.#strings.choose, ''));
      const group = (value) => /\d m$/.test(sizeKey(value)) ? 0 : /\d y$/.test(sizeKey(value)) ? 1 : 2;
      const collator = new Intl.Collator(document.documentElement.lang, { numeric: true });
      const values = sizes.values.filter((value) => value.count > 0 && !this.#ownSizes.has(sizeKey(value.value))).sort((a, b) => group(a.value) - group(b.value) || collator.compare(a.label, b.label));
      for (const value of values) this.refs.size.add(new Option(value.label, value.value));
      this.#ready = true;
      this.refs.form.hidden = !values.length;
      this.refs.status.textContent = values.length ? '' : this.#strings.no_sizes;
    } catch (error) {
      if (error.name !== 'AbortError' && this.isConnected) this.refs.status.textContent = this.#strings.error;
    }
  }

  clearResults() {
    this.#request?.abort();
    this.refs.results.replaceChildren();
    this.refs.status.textContent = '';
    this.refs.submit.disabled = false;
    this.refs.results.removeAttribute('aria-busy');
  }

  /** @param {SubmitEvent} event - Explicit request for a missing size, independent of purchase controls. */
  async findAlternatives(event) {
    event.preventDefault();
    if (!this.#ready || !this.refs.size.value) return;
    this.clearResults();
    const controller = new AbortController();
    this.#request = controller;
    const requested = this.refs.size.value;
    const label = this.refs.size.selectedOptions[0].textContent;
    this.refs.status.textContent = this.#strings.loading;
    this.refs.submit.disabled = true;
    this.refs.results.setAttribute('aria-busy', 'true');
    const price = Number(this.dataset.price);
    const min = Math.floor(price * 0.6);
    const max = Math.ceil(price * 1.4);
    const url = new URL(this.#base);
    url.searchParams.set(this.#sizeParam, requested);
    url.searchParams.set('filter.v.price.gte', (min / 100).toFixed(2));
    url.searchParams.set('filter.v.price.lte', (max / 100).toFixed(2));
    url.searchParams.set('section_id', 'bp-size-alternative-results');
    let next = url;
    const found = new Map();
    try {
      for (let page = 0; next && page < 3; page++) {
        const response = await fetch(next, { signal: controller.signal });
        if (!response.ok) throw new Error('results');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = html.querySelector('[data-size-alternative-results]');
        if (!root) throw new Error('results');
        for (const candidate of root.querySelectorAll('[data-alternative-product]')) {
          if (candidate.dataset.id === this.dataset.productId || candidate.dataset.kind !== this.dataset.kind || normalise(candidate.dataset.type) !== normalise(this.dataset.productType)) continue;
          const variants = JSON.parse(candidate.querySelector('[data-variants]').textContent);
          const variant = variants.find((item) => sizeKey(item.size) === sizeKey(requested) && item.price >= min && item.price <= max);
          const link = candidate.querySelector('[data-product-link]');
          if (!variant || !link) continue;
          const destination = new URL(link.getAttribute('href'), location.origin);
          if (destination.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(destination.pathname)) continue;
          destination.searchParams.set('variant', String(variant.id));
          found.set(candidate.dataset.id, { candidate, variant, destination, sameColor: this.dataset.color && candidate.dataset.color === this.dataset.color });
        }
        if (found.size >= 3) break;
        const nextHref = root.querySelector('[data-next-page]')?.getAttribute('href');
        next = nextHref ? new URL(nextHref, location.origin) : null;
        if (next && (next.origin !== location.origin || next.pathname !== this.#base.pathname)) next = null;
        next?.searchParams.set('section_id', 'bp-size-alternative-results');
      }
      if (!this.isConnected || controller.signal.aborted) return;
      const matches = [...found.values()].sort((a, b) => Number(b.sameColor) - Number(a.sameColor) || Math.abs(a.variant.price - price) - Math.abs(b.variant.price - price)).slice(0, 3);
      for (const match of matches) this.#append(match, label);
      this.refs.status.textContent = (matches.length ? this.#strings.found : this.#strings.empty).replace('__SIZE__', label);
    } catch (error) {
      if (error.name !== 'AbortError' && this.isConnected) this.refs.status.textContent = this.#strings.error;
    } finally {
      if (this.isConnected && !controller.signal.aborted) {
        this.refs.submit.disabled = false;
        this.refs.results.removeAttribute('aria-busy');
      }
    }
  }

  #append({ candidate, variant, destination }, label) {
    const returningValue = new URL(location.href).searchParams.get('bp_return_to');
    if (returningValue && returningValue.length <= 6000) {
      try {
        const returning = new URL(returningValue, location.origin);
        const prefix = `${this.dataset.collectionsUrl.replace(/\/$/, '')}/`;
        const handle = returning.pathname.startsWith(prefix) ? returning.pathname.slice(prefix.length) : '';
        if (returning.origin === location.origin && (/^[a-z0-9-]+\/?$/.test(handle) || returning.pathname === this.dataset.searchUrl)) {
          returning.searchParams.delete('bp_return_to');
          destination.searchParams.set('bp_return_to', returning.pathname + returning.search + returning.hash);
        }
      } catch {}
    }
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = destination.pathname + destination.search;
    const image = candidate.querySelector('img');
    if (image) link.append(image.cloneNode(true));
    const body = document.createElement('div');
    for (const [className, text] of [
      ['name', candidate.querySelector('[data-product-link]').textContent],
      ['size', this.#strings.size.replace('__SIZE__', label)], ['price', variant.money], ['view', this.#strings.view],
    ]) {
      const span = document.createElement('span');
      span.className = `bp-alternatives__${className}`;
      span.textContent = text;
      body.append(span);
    }
    const code = candidate.querySelector('[data-product-code] .bp-card-codes');
    if (code) body.querySelector('.bp-alternatives__name').after(code.cloneNode(true));
    link.append(body);
    item.append(link);
    this.refs.results.append(item);
  }
}

if (!customElements.get('bp-size-alternatives')) customElements.define('bp-size-alternatives', SizeAlternatives);
